import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Grid,
  Input,
  SpaceBetween,
  Textarea,
  Toggle,
  TokenGroup,
} from '@cloudscape-design/components';
import { Division } from 'src/types/orbit/Division';
import { Location } from 'src/types/helios/Location';
import { useEffect, useState } from 'react';
import useUserProfile from 'src/hooks/useUserProfile';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { AuditExpirationSettings } from 'src/types/helios/AuditTemplate';
import { useDivision } from 'src/store/division/useDivision';
import { useFormValidation } from 'src/hooks/useFormValidation';
import {
  AuditTemplateValidator,
  AuditTemplateValidationPaths,
} from 'src/validations/AuditTemplateValidator';
import SingleSelectDivision from '../division/SingleSelectDivision';
import MultiSelectTags from '../tag/MultiSelectTags';
import { useTemplates } from 'src/store/templates/useTemplates';

import { useTags } from 'src/store/tags/useTags';
import RequiredFieldLabel from '../RequiredFieldLabel';
import LocationSelectionPanel from '../location/LocationSelectionPanel';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { useLocations } from 'src/store/locations/useLocations';
import { TagMap } from 'src/types/helios/Question';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import QuestionSelection from '../QuestionSelection/QuestionSelection';
import WarningModal from '../WarningModal';
import { transformCodesToLocations, extractCodes } from 'src/helper/locationHelper';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';

interface TemplateParams {
  name: string;
  description: string;
  division: string;
  locationsIncluded?: string[];
  questionsExcluded?: string[];
  tags: TagMap;
  isActive: boolean;
  businessUnit: string;
  reason: string;
  auditExpirationSettings?: AuditExpirationSettings;
}

interface TemplateFormProps {
  auditTemplate?: AuditTemplate | null;
  dismissView: () => void;
}

const TemplateForm = ({ auditTemplate, dismissView }: TemplateFormProps) => {
  const { getUserId, getPersona, getUserBusinessUnitId, getUserProgramId } =
    useUserProfile();

  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();

  const { locationsList, loadLocations } = useLocations();
  const {
    createTemplate,
    updateTemplate,
    createTemplateApiStatus,
    updateTemplateApiStatus,
    resetApiStatus,
  } = useTemplates();

  const { loadTags, tagsList, fetchTagsApiStatus } = useTags();

  const initValues: TemplateParams = {
    name: auditTemplate?.templateName || '',
    division: auditTemplate?.divisionName || '',
    tags: auditTemplate?.tags || {},
    locationsIncluded: auditTemplate?.locationsIncluded || [],
    questionsExcluded: auditTemplate?.questionsExcluded || [],
    description: auditTemplate?.description || '',
    isActive: auditTemplate?.isActive ?? true,
    businessUnit: auditTemplate?.businessUnit || getUserBusinessUnitId(),
    auditExpirationSettings: auditTemplate?.auditExpirationSettings || {
      months: 0,
      days: 30,
    },
    reason: '',
  };

  const [templateParams, setTemplateParams] = useState<TemplateParams>(initValues);
  const { setContent, setTitle, closeView: closeSplitPanel, isOpen } = useSplitPanel();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<boolean | null>(null);
  const { fieldValidationError, validate } = useFormValidation(
    templateParams,
    AuditTemplateValidator(tagsList),
  );
  const [questionTags, setQuestionTags] = useState<TagMap | undefined>(undefined);
  //Load initial data
  useEffect(() => {
    loadBusinessUnits({
      programId: getUserProgramId(),
      isActive: true,
      isDeleted: false,
    });
    loadTags({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId()!,
    });
    loadLocations({
      isEnabledForBU: true,
      businessUnitId: getUserBusinessUnitId(),
    });

    return () => {
      closeSplitPanel();
    };
  }, []);
  useEffect(() => {
    if (templateParams.businessUnit) {
      loadDivisionsByBusinessUnit({
        businessUnitId: templateParams.businessUnit,
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
    }
  }, [templateParams.businessUnit]);

  // Handle API status changes
  useEffect(() => {
    if (createTemplateApiStatus.isSuccess || updateTemplateApiStatus.isSuccess) {
      dismissView(); // Close the form after successful API call
    }
    return () => {
      resetApiStatus();
    };
  }, [createTemplateApiStatus.isSuccess, updateTemplateApiStatus.isSuccess]);

  const renderActionButton = () => {
    const onCancelClick = () => {
      dismissView(); // Close the form without saving any changes
    };

    const onSaveClick = () => {
      if (validate()) {
        const userId = getUserId();
        if (auditTemplate) {
          // Update existing template
          updateTemplate({
            templateName: templateParams.name,
            description: templateParams.description,
            tags: templateParams.tags,
            isActive: templateParams.isActive,
            businessUnitId: templateParams.businessUnit,
            divisionName: templateParams.division,
            locationsIncluded: templateParams.locationsIncluded,
            questionsExcluded: templateParams.questionsExcluded,
            program: getUserProgramId(),
            lastUpdatedBy: userId,
            auditExpirationSettings: templateParams.auditExpirationSettings!,
            reason: templateParams.reason,
          });
        } else {
          // Create new template
          createTemplate({
            templateName: templateParams.name,
            description: templateParams.description,
            tags: templateParams.tags,
            isActive: templateParams.isActive,
            businessUnitId: templateParams.businessUnit,
            divisionName: templateParams.division,
            locationsIncluded: templateParams.locationsIncluded,
            questionsExcluded: templateParams.questionsExcluded,
            program: getUserProgramId(),
            createdBy: userId,
            auditExpirationSettings: templateParams.auditExpirationSettings!,
          });
        }
      }
    };

    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="cancel-button"
          formAction="none"
          variant="link"
          onClick={onCancelClick}
        >
          Cancel
        </Button>

        <Button
          data-testid="save-button"
          variant="primary"
          onClick={onSaveClick}
          loading={createTemplateApiStatus.isLoading || updateTemplateApiStatus.isLoading}
        >
          Save
        </Button>
      </SpaceBetween>
    );
  };

  return (
    <div>
      {questionTags && Object.entries(questionTags).length > 0 ? (
        <QuestionSelection
          tags={questionTags}
          data={auditTemplate}
          division={templateParams.division}
          selectedQuestions={templateParams.questionsExcluded || []}
          onViewDismiss={function (): void {
            setQuestionTags(undefined);
          }}
          onSave={function (unselectedQuestionIds: string[]): void {
            setTemplateParams((prevState) => ({
              ...prevState,
              questionsExcluded: unselectedQuestionIds,
            }));
            setQuestionTags(undefined);
          }}
        />
      ) : (
        <Container>
          <form onSubmit={(e) => e.preventDefault()}>
            <WarningModal
              visible={showDeactivateModal}
              title="⚠️ Warning:"
              message="Disabling this template is irreversible. All audits scheduled for this template will be cancelled. No new audits can be created from a disabled template. Existing audits (Not Started & In Progress) will not be affected.  Are you sure you wish to continue?"
              onOk={() => {
                if (pendingStatusChange !== null) {
                  setTemplateParams((prev) => ({
                    ...prev,
                    isActive: pendingStatusChange,
                  }));
                }
                setShowDeactivateModal(false);
                setPendingStatusChange(null);
              }}
              onCancel={() => {
                setShowDeactivateModal(false);
                setPendingStatusChange(null);
              }}
            />

            <WarningModal
              visible={showActivateModal}
              title="Information:"
              message="New audits will be created based on created/existing schedules on the next execution day."
              onOk={() => {
                if (pendingStatusChange !== null) {
                  setTemplateParams((prev) => ({
                    ...prev,
                    isActive: pendingStatusChange,
                  }));
                }
                setShowActivateModal(false);
                setPendingStatusChange(null);
              }}
              onCancel={() => {
                setShowActivateModal(false);
                setPendingStatusChange(null);
              }}
            />

            <ApiStatusFlashBar
              apiStatus={[createTemplateApiStatus, updateTemplateApiStatus]}
              clearMessages={() => {
                resetApiStatus();
              }}
            />

            <Form actions={renderActionButton()}>
              <ColumnLayout columns={1}>
                <SpaceBetween direction="vertical" size="l">
                  <FormField
                    data-testid="name-field"
                    label={<RequiredFieldLabel label="Template Name" />}
                    errorText={
                      fieldValidationError[AuditTemplateValidationPaths.NAME.path]
                    }
                  >
                    <Input
                      data-testid="name-input"
                      value={templateParams!.name}
                      readOnly={auditTemplate != undefined}
                      onChange={({ detail }) =>
                        setTemplateParams((prevState) => ({
                          ...prevState,
                          name: detail.value,
                        }))
                      }
                    />
                  </FormField>

                  <FormField
                    data-testid="description-field"
                    label="Description"
                    errorText={
                      fieldValidationError[AuditTemplateValidationPaths.DISCRIPTION.path]
                    }
                  >
                    <Textarea
                      data-testid="description-input"
                      value={templateParams.description}
                      onChange={({ detail }) =>
                        setTemplateParams((prevState) => ({
                          ...prevState,
                          description: detail.value,
                        }))
                      }
                    />
                  </FormField>

                  <FormField
                    data-testid="busniess-unti-field"
                    label={<RequiredFieldLabel label="Business Unit" />}
                    errorText={fieldValidationError.businessUnit}
                  >
                    <Input
                      data-testid="businessUnit-input"
                      value={
                        businessUnitList.find(
                          (bu) => bu.businessUnitId === templateParams!.businessUnit,
                        )?.name || templateParams!.businessUnit!
                      }
                      readOnly
                    />
                  </FormField>

                  <FormField
                    data-testid="division-field"
                    label={<RequiredFieldLabel label="Division" />}
                    errorText={fieldValidationError.division}
                  >
                    <SingleSelectDivision
                      divisions={divisionList}
                      readOnly={auditTemplate != undefined}
                      isLoading={fetcDivisionsApiStatus.isLoading}
                      error={fetcDivisionsApiStatus.errorMessage}
                      selectedDivision={templateParams.division}
                      onItemSelection={(item: Division | null) => {
                        setTemplateParams((prevState) => ({
                          ...prevState,
                          division: item?.divisionName || '',
                        }));
                      }}
                    />
                  </FormField>
                  <FormField
                    label={<RequiredFieldLabel label="Activation Status" />}
                    constraintText="Enable or disable the template using this switch."
                  >
                    <Toggle
                      data-testid="template-active-status-toggle"
                      onChange={({ detail }) => {
                        const newStatus = detail.checked;

                        if (!newStatus && templateParams.isActive) {
                          // Deactivating - show warning modal
                          setPendingStatusChange(newStatus);
                          setShowDeactivateModal(true);
                        } else if (newStatus && !templateParams.isActive) {
                          // Activating - show info modal
                          setPendingStatusChange(newStatus);
                          setShowActivateModal(true);
                        } else {
                          // Direct change for other cases
                          setTemplateParams((prevState) => ({
                            ...prevState,
                            isActive: newStatus,
                          }));
                        }
                      }}
                      checked={templateParams.isActive}
                    >
                      {templateParams.isActive ? 'Active' : 'Inactive'}
                    </Toggle>
                  </FormField>

                  <FormField
                    data-testid="location-field"
                    label={<RequiredFieldLabel label="Location" />}
                    errorText={fieldValidationError.locationsIncluded}
                  >
                    <Button
                      onClick={() => {
                        setTitle('Select Locations');
                        setContent(
                          <LocationSelectionPanel
                            selectedLocations={transformCodesToLocations(
                              templateParams.locationsIncluded || [],
                              locationsList,
                            )}
                            onChange={(locations: Location[]) => {
                              setTemplateParams((prevState) => ({
                                ...prevState,
                                locationsIncluded: extractCodes(locations),
                              }));
                            }}
                            locations={locationsList}
                          />,
                        );
                      }}
                    >
                      Add Location
                    </Button>

                    <TokenGroup
                      onDismiss={
                        isOpen
                          ? undefined
                          : ({ detail: { itemIndex } }) => {
                              setTemplateParams((prev) => ({
                                ...prev,
                                locationsIncluded: [
                                  ...(prev.locationsIncluded || []).slice(0, itemIndex),
                                  ...(prev.locationsIncluded || []).slice(itemIndex + 1),
                                ],
                              }));
                            }
                      }
                      items={(templateParams.locationsIncluded || []).map((code) => ({
                        value: code,
                        label: code,
                        dismissLabel: isOpen ? '' : `Remove ${code}`,
                      }))}
                    />
                  </FormField>
                  {(() => {
                    const missingTags = tagsList.filter(
                      (tag) =>
                        tag.isMandatoryForAuditTemplates &&
                        tag.tagGroupName &&
                        !templateParams.tags?.[tag.tagGroupName],
                    );
                    return (
                      missingTags.length > 0 && (
                        <Alert type="warning" header="Missing mandatory tags">
                          Please select:{' '}
                          {missingTags.map((tag) => tag.tagGroupName).join(', ')}
                        </Alert>
                      )
                    );
                  })()}

                  {(() => {
                    const maxLimitTags = tagsList?.filter(
                      (tag) =>
                        tag.maxTagsPerTemplate &&
                        tag.tagGroupName &&
                        templateParams.tags?.[tag.tagGroupName] &&
                        templateParams.tags[tag.tagGroupName].length >
                          tag.maxTagsPerTemplate,
                    );
                    return (
                      maxLimitTags?.length > 0 && (
                        <Alert type="error" header="Maximum tag values is reached">
                          Tags:{' '}
                          {maxLimitTags
                            ?.map(
                              (tag) =>
                                `${tag.tagGroupName}(${templateParams.tags?.[tag.tagGroupName!].length}/${tag.maxTagsPerTemplate})`,
                            )
                            .join(', ')}
                        </Alert>
                      )
                    );
                  })()}

                  <FormField
                    data-testid="tags-field"
                    label={<RequiredFieldLabel label="Associated Tags" />}
                    errorText={fieldValidationError.tags}
                  >
                    <Grid
                      gridDefinition={[
                        { colspan: { default: 8, xs: 7 } },
                        { colspan: { default: 4, xs: 5 } },
                      ]}
                    >
                      <MultiSelectTags
                        tags={tagsList}
                        isLoading={fetchTagsApiStatus.isLoading}
                        error={fetchTagsApiStatus.errorMessage}
                        selectedTagValues={Object.entries(templateParams.tags || {}).map(
                          ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
                        )}
                        onItemSelection={(selectedTags) => {
                          const tagsMap: Record<string, string[]> = {};
                          selectedTags.forEach((tag) => {
                            tagsMap[tag.tagGroupName] = tag.tagValues;
                          });
                          setTemplateParams((prev) => ({ ...prev, tags: tagsMap }));
                        }}
                      />

                      {Object.entries(templateParams.tags).length > 0 && (
                        <Button
                          data-testid="button-view_question"
                          onClick={() => {
                            setQuestionTags(templateParams.tags);
                          }}
                          iconName="external"
                        >
                          View Questions
                        </Button>
                      )}
                    </Grid>
                  </FormField>

                  <FormField
                    data-testid="audit-expiration-field"
                    label={<RequiredFieldLabel label="Audit Expiration Settings" />}
                    description="Set the expiration period for audits created from this template."
                    errorText={fieldValidationError.auditExpirationSettings}
                  >
                    <ColumnLayout columns={2}>
                      <FormField label="Months">
                        <Input
                          data-testid="expiration-months-input"
                          type="number"
                          value={
                            templateParams.auditExpirationSettings?.months?.toString() ||
                            ''
                          }
                          onChange={({ detail }) =>
                            setTemplateParams((prevState) => ({
                              ...prevState,
                              auditExpirationSettings: {
                                ...prevState.auditExpirationSettings!,
                                months: parseInt(detail.value),
                              },
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Days">
                        <Input
                          data-testid="expiration-days-input"
                          type="number"
                          value={
                            templateParams.auditExpirationSettings?.days?.toString() || ''
                          }
                          onChange={({ detail }) =>
                            setTemplateParams((prevState) => ({
                              ...prevState,
                              auditExpirationSettings: {
                                ...prevState.auditExpirationSettings!,
                                days: parseInt(detail.value),
                              },
                            }))
                          }
                        />
                      </FormField>
                    </ColumnLayout>
                  </FormField>
                </SpaceBetween>
              </ColumnLayout>
            </Form>
          </form>
        </Container>
      )}
    </div>
  );
};

export default TemplateForm;
